'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';

import type {
  GeneralLayout,
  Menu,
} from '@eliadmin/core/src/api/general/types/general-layout-types';

import { MobileSearchProvider } from '@eliadmin/core/src/components/header/mobile-search';
import MobileSidebar from '@eliadmin/core/src/components/header/mobile-sidebar';
import { PriceOfferDialog } from '../ui/price-offer-dialog';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Heading } from '../ui/heading';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref } from '@eliadmin/core/src/utils/url-utils';
import { GoArrowRight } from 'react-icons/go';
import { Service } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export type HeaderMainProps = {
  generalLayout: GeneralLayout['data'];
  menu: Menu;
  services: Service[];
};

export function HeaderMain({ generalLayout, menu, services }: HeaderMainProps) {
  const { t } = useTranslations();
  const pathname = usePathname();
  const router = useRouter();

  const [open, setOpen] = useState(false);

  const plusMenuIndex = 2;

  const handleServiceClick = (href: string) => {
    router.push(href);
    setTimeout(() => {
      setOpen(false);
    }, 100);
  };

  return (
    <MobileSearchProvider>
      <div id="main-header" className={cn('custom-section', 'bg-white', 'ea-header-main')}>
        <div
          className={cn(
            'custom-container--xxl',
            'flex h-14 items-center justify-between sm:h-auto sm:py-4 md:py-0 xl:pr-8',
          )}
        >
          <Link prefetchOnHover
            href={ROUTES.HOME()}
            className="cursor-pointer transition-all hover:opacity-75"
          >
            <Image
              src={generalLayout.header?.logo?.url ?? ''}
              alt={resolveImageAlt(generalLayout?.header?.logo, generalLayout?.contact?.company ?? '')}
              width={0}
              height={0}
              className="h-8 w-auto object-contain sm:h-9 lg:h-10 xl:h-[50px]"
            />
          </Link>

          <div className="hidden items-center justify-end gap-6 md:flex xl:gap-8">
            <nav className="flex items-center justify-end gap-0">
              {menu.fields?.map((item, index) => {
                const isPlus = index === plusMenuIndex;
                const isActive = pathname === getSafeHref(item?.path ?? '#');

                if (!isPlus) {
                  return (
                    <Link prefetchOnHover
                      key={item.name}
                      href={getSafeHref(item?.path ?? '#')}
                      className={cn(
                        'flex h-[85px] items-center justify-center px-4 text-sm font-normal leading-[1.3] text-black transition-all lg:text-base lg:leading-[1.3] xl:px-6',
                        {
                          'ea-header-main__menu-item--active font-bold': isActive,
                          'hover:text-opacity-75': !isActive,
                        },
                      )}
                    >
                      {item.name}
                    </Link>
                  );
                }

                return (
                  <HoverMenu
                    open={open}
                    setOpen={setOpen}
                    key={item.name}
                    trigger={
                      <div className="flex h-[85px] cursor-pointer items-center justify-center px-4 text-sm font-normal lg:text-base xl:px-6">
                        {item.name}
                      </div>
                    }
                  >
                    <div className="grid w-full grid-cols-3 gap-6 lg:max-w-[1149px]">
                      {services.map(service => (
                        <ServiceCard
                          key={service.slug}
                          service={service}
                          onClick={handleServiceClick}
                        />
                      ))}
                    </div>
                  </HoverMenu>
                );
              })}
            </nav>

            <PriceOfferDialog className="hidden xl:block">
              <Button variant="dark" className="rounded-full">
                {t('text1117')}
              </Button>
            </PriceOfferDialog>
          </div>

          <div className="flex items-center justify-center gap-4 md:hidden lg:gap-6">
            <MobileSidebar menu={menu.fields} side="right" services={services} />
          </div>
        </div>
      </div>
    </MobileSearchProvider>
  );
}

function ServiceCard({ service, onClick }: { service: Service; onClick: (href: string) => void }) {
  const href = service.slug;

  return (
    <button
      onClick={() => {
        onClick(href);
      }}
      className="group relative col-span-1 flex w-full cursor-pointer items-center justify-between rounded-[8px] border border-border bg-white px-4 py-4 transition-all hover:bg-neutral-100 sm:px-4 md:p-6 xl:p-8"
    >
      <Heading tag="h5" title={service.name} className="relative z-10 text-left" />
      <Image
        src={service?.image?.url ?? ''}
        alt={resolveImageAlt(service?.image, service?.name)}
        width={0}
        height={0}
        className="relative z-10 max-h-10 min-h-10 min-w-10 max-w-10 object-contain md:max-h-12 md:min-h-12 md:min-w-12 md:max-w-12"
        sizes="100vw"
      />
      <div
        className={cn(
          'absolute inset-0 z-[1] h-full w-full bg-white/35 opacity-0 transition-all',
          'group-hover:opacity-100',
        )}
      />
      <Image
        src={service?.images?.[0]?.url ?? '/images/placeholder.png'}
        alt={resolveImageAlt(service?.images?.[0], service?.name)}
        width={0}
        height={0}
        className={cn(
          'absolute inset-0 z-0 h-full w-full object-cover object-center opacity-0 transition-all',
          'group-hover:opacity-100',
        )}
        sizes="100vw"
      />
    </button>
  );
}

function HoverMenu({
  trigger,
  children,
  open,
  setOpen,
}: {
  trigger: React.ReactNode;
  children: React.ReactNode;
  open: boolean;
  setOpen: (isOpen: boolean) => void;
}) {
  return (
    <div onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      {trigger}
      <div
        className={cn(
          'custom-section',
          'absolute left-0 top-full z-[40] w-full bg-white py-12 shadow-xl transition-all duration-200 lg:py-16',
          open
            ? 'pointer-events-auto translate-y-0 opacity-100'
            : 'pointer-events-none -translate-y-2 opacity-0',
        )}
      >
        <div className={cn('custom-container--xxl', 'flex items-stretch justify-between gap-6')}>
          {children}
          <div className="relative z-10 hidden w-full flex-col items-start justify-between gap-4 rounded-[8px] bg-[#061E72] p-4 sm:max-w-[427px] sm:p-6 lg:gap-8 lg:p-8 xl:flex">
            <Heading
              title="Chcem cenovú ponuku na mieru"
              tag="h4"
              className="w-full max-w-[250px] text-balance text-white sm:max-w-[275px]"
            />
            <PriceOfferDialog>
              <Button variant={'white'} size={'icon'} className="rounded-full" aria-label="Chcem cenovú ponuku na mieru">
                <GoArrowRight size={20} />
              </Button>
            </PriceOfferDialog>
          </div>
        </div>
      </div>
    </div>
  );
}
